import React, { useState } from 'react';
import { FaTimes, FaSave, FaDollarSign } from 'react-icons/fa';
import './AbonarCompraModal.css';
import comprasService from '../../services/ComprasService';

const AbonarCompraModal = ({ isOpen, onClose, compra, onAbonoCreado }) => {
  const [formData, setFormData] = useState({
    monto: '',
    referencia: '',
    fecha: new Date().toISOString().split('T')[0]
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calcular monto sugerido (saldo pendiente)
  const getMontoSugerido = () => {
    if (!compra) return 0;
    return Math.max(0, compra.saldo || 0);
  };

  // Usar monto sugerido
  const usarMontoSugerido = () => {
    setFormData(prev => ({
      ...prev,
      monto: getMontoSugerido().toString()
    }));
  };

  // Guardar abono
  const guardarAbono = async () => {
    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }

    if (!formData.referencia.trim()) {
      setError('La referencia es requerida');
      return;
    }

    if (parseFloat(formData.monto) > getMontoSugerido()) {
      setError('El monto no puede ser mayor al saldo pendiente');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const abonoData = {
        idCompra: compra.id,
        monto: parseFloat(formData.monto),
        referencia: formData.referencia.trim(),
        fecha: formatearFecha(formData.fecha)
      };

      await comprasService.crearAbono(abonoData);
      
      // Notificar éxito y cerrar modal
      onAbonoCreado && onAbonoCreado();
      cerrarModal();
      
    } catch (error) {
      setError('Error al registrar abono: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Formatear fecha para envío sin afectar zona horaria
  const formatearFecha = (fechaISO) => {
    if (!fechaISO) return '';
    const [año, mes, dia] = fechaISO.split('-');
    return `${dia}/${mes}/${año}`;
  };

  // Cerrar modal y limpiar formulario
  const cerrarModal = () => {
    setFormData({
      monto: '',
      referencia: '',
      fecha: new Date().toISOString().split('T')[0]
    });
    setError('');
    onClose();
  };

  if (!isOpen || !compra) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container abonar-compra-modal">
        <div className="modal-header">
          <h2>
            <FaDollarSign /> Abonar a Compra
          </h2>
          <button className="btn-close" onClick={cerrarModal}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Información de la compra */}
          <div className="compra-info">
            <h3>Información de la Compra</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Compra:</label>
                <span>{compra.compra}</span>
              </div>
              <div className="info-item">
                <label>Proveedor:</label>
                <span>{compra.proveedor}</span>
              </div>
              <div className="info-item">
                <label>Referencia:</label>
                <span>{compra.referencia}</span>
              </div>
              <div className="info-item">
                <label>Fecha:</label>
                <span>{compra.fecha}</span>
              </div>
              <div className="info-item">
                <label>Monto Total:</label>
                <span className="monto-total">${compra.monto?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="info-item">
                <label>Abonado:</label>
                <span className="monto-abonado">${compra.abonado?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="info-item">
                <label>Saldo Pendiente:</label>
                <span className="saldo-pendiente">${compra.saldo?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="info-item">
                <label>Estatus:</label>
                <span className={`estatus ${compra.estatus?.toLowerCase()}`}>
                  {compra.estatus}
                </span>
              </div>
            </div>
          </div>

          {/* Formulario de abono */}
          <form className="abono-form">
            <div className="form-row">
              <div className="form-group">
                <label>Monto del Abono *</label>
                <div className="monto-container">
                  <input
                    type="number"
                    name="monto"
                    value={formData.monto}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0.01"
                    max={getMontoSugerido()}
                    step="0.01"
                    required
                    className="monto-input"
                  />
                  {getMontoSugerido() > 0 && (
                    <button
                      type="button"
                      className="btn-monto-sugerido"
                      onClick={usarMontoSugerido}
                    >
                      Pagar todo (${getMontoSugerido().toFixed(2)})
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Fecha del Abono *</label>
                <div className="fecha-input-container">
                  <input
                    type="date"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleInputChange}
                    required
                    className="fecha-input"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Referencia *</label>
              <input
                type="text"
                name="referencia"
                value={formData.referencia}
                onChange={handleInputChange}
                placeholder="Número de comprobante, transferencia, etc."
                required
              />
            </div>
          </form>

          {/* Resumen del abono */}
          {formData.monto && parseFloat(formData.monto) > 0 && (
            <div className="resumen-abono">
              <h4>Resumen del Abono</h4>
              <div className="resumen-grid">
                <div className="resumen-item">
                  <label>Monto a abonar:</label>
                  <span className="valor-abono">${parseFloat(formData.monto).toFixed(2)}</span>
                </div>
                <div className="resumen-item">
                  <label>Saldo después del abono:</label>
                  <span className="saldo-resultante">
                    ${Math.max(0, getMontoSugerido() - parseFloat(formData.monto)).toFixed(2)}
                  </span>
                </div>
                <div className="resumen-item">
                  <label>Nuevo estatus:</label>
                  <span className={`nuevo-estatus ${
                    parseFloat(formData.monto) >= getMontoSugerido() ? 'pagado' : 'parcial'
                  }`}>
                    {parseFloat(formData.monto) >= getMontoSugerido() ? 'PAGADO' : 'PARCIAL'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn-cancelar" 
            onClick={cerrarModal}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn-guardar" 
            onClick={guardarAbono}
            disabled={loading || !formData.monto || !formData.referencia}
          >
            <FaSave /> {loading ? 'Guardando...' : 'Registrar Abono'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AbonarCompraModal;